import React, { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Experience,
  createExperience,
  updateExperience,
  deleteExperience,
  moveExperienceUp,
  moveExperienceDown,
} from "../firebase/experienceService";
import { Timestamp } from "firebase/firestore";
import { globalRateLimiter } from "../utils/rateLimiter";
import { experiencesQuery } from "../queries/contentQueries";

interface ExperienceContextType {
  experiences: Experience[];
  loading: boolean;
  error: Error | null;
  refreshExperiences: () => Promise<void>;
  addExperience: (
    experience: Omit<Experience, "id" | "createdAt" | "updatedAt">
  ) => Promise<string>;
  editExperience: (
    id: string,
    experience: Partial<Omit<Experience, "id" | "createdAt" | "updatedAt">>
  ) => Promise<void>;
  removeExperience: (id: string) => Promise<void>;
  moveUp: (id: string) => Promise<void>;
  moveDown: (id: string) => Promise<void>;
}

const EMPTY: Experience[] = [];

// Data lives in the TanStack Query cache; the provider is kept so the app tree doesn't change.
export const ExperienceProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => <>{children}</>;

export const useExperiences = (): ExperienceContextType => {
  const queryClient = useQueryClient();
  const { data, isPending, error, refetch } = useQuery(experiencesQuery);

  const setExperiences = (update: (prev: Experience[]) => Experience[]) =>
    queryClient.setQueryData<Experience[]>(experiencesQuery.queryKey, (prev) =>
      update(prev ?? [])
    );

  const refreshExperiences = async () => {
    await refetch();
  };

  const addExperience = async (
    experience: Omit<Experience, "id" | "createdAt" | "updatedAt">
  ): Promise<string> => {
    // Rate limiting for experience operations
    if (!globalRateLimiter.isAllowed("add-experience")) {
      throw new Error(
        "Too many requests. Please wait a moment before trying again."
      );
    }

    const id = await createExperience(experience);
    setExperiences((prev) => [
      ...prev,
      { ...experience, id, createdAt: Timestamp.now(), updatedAt: Timestamp.now() },
    ]);
    return id;
  };

  const editExperience = async (
    id: string,
    experience: Partial<Omit<Experience, "id" | "createdAt" | "updatedAt">>
  ): Promise<void> => {
    // Rate limiting for experience updates
    if (!globalRateLimiter.isAllowed(`edit-experience-${id}`)) {
      throw new Error(
        "Too many update requests. Please wait a moment before trying again."
      );
    }

    await updateExperience(id, experience);
    setExperiences((prev) =>
      prev.map((e) =>
        e.id === id
          ? ({ ...e, ...experience, updatedAt: Timestamp.now() } as Experience)
          : e
      )
    );
  };

  const removeExperience = async (id: string): Promise<void> => {
    await deleteExperience(id);
    setExperiences((prev) => prev.filter((e) => e.id !== id));
  };

  // Move operations change ordering server-side, so refetch
  const moveUp = async (id: string): Promise<void> => {
    await moveExperienceUp(id);
    await refreshExperiences();
  };

  const moveDown = async (id: string): Promise<void> => {
    await moveExperienceDown(id);
    await refreshExperiences();
  };

  return {
    experiences: data ?? EMPTY,
    loading: isPending,
    error: error as Error | null,
    refreshExperiences,
    addExperience,
    editExperience,
    removeExperience,
    moveUp,
    moveDown,
  };
};
